import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { fetchMe, logout as apiLogout, updateMyTimezone } from "@/lib/api";

const AuthContext = createContext({ user: null, loading: true, refresh: () => {}, signOut: () => {} });

function detectBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const u = await fetchMe();
      setUser(u);
      // One-time browser-tz sync: if backend's stored tz differs from the
      // browser's resolved tz, update it. This catches the "I just signed up"
      // case AND any user who's travelling / on a new device.
      const browserTz = detectBrowserTimezone();
      if (browserTz && u?.timezone && browserTz !== u.timezone) {
        // Only update if we haven't already nudged this session
        const key = `kidhq.tz.synced.${u.user_id}.${browserTz}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          updateMyTimezone(browserTz).then(({ timezone }) => {
            setUser(prev => prev ? { ...prev, timezone } : prev);
          }).catch(() => {});
        }
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try { await apiLogout(); } catch { /* ignore */ }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, setUser, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
